import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Scale, Plus, Search, Edit2, Trash2, X, AlertCircle, Loader2, CheckCircle, Calculator,
} from 'lucide-react';
import { getAuthHeaders } from '../utils/authHeaders';

const API_BASE = process.env.REACT_APP_API_URL || 'https://predworks.alliantprojects.com/api/v1';

type ActionType = 'AS' | 'TS' | 'TENDER_ACCEPTANCE';

interface Slab {
  id: number;
  scheme: string;
  action_type: ActionType;
  min_cost: number | string;
  max_cost: number | string | null;
  authority: string;
  is_active: boolean;
}

type SlabForm = Partial<Slab>;

const ACTIONS: { value: ActionType; label: string }[] = [
  { value: 'AS', label: 'Administrative Sanction (AS)' },
  { value: 'TS', label: 'Technical Sanction (TS)' },
  { value: 'TENDER_ACCEPTANCE', label: 'Tender Acceptance' },
];
const ACTION_LABEL: Record<ActionType, string> = {
  AS: 'Administrative Sanction', TS: 'Technical Sanction', TENDER_ACCEPTANCE: 'Tender Acceptance',
};
const ACTION_BADGE: Record<ActionType, string> = {
  AS: 'bg-amber-100 text-amber-700', TS: 'bg-blue-100 text-blue-700', TENDER_ACCEPTANCE: 'bg-purple-100 text-purple-700',
};
const AUTHORITY_SUGGESTIONS = ['Gram Panchayat', 'MPP', 'ZP', 'AEE', 'EE', 'SE', 'CE', 'ENC', 'Government'];

const EMPTY_FORM: SlabForm = {
  scheme: 'ALL', action_type: 'AS', min_cost: 0, max_cost: null, authority: '', is_active: true,
};

async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders(), ...(options.headers || {}) },
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error(e.message ? (Array.isArray(e.message) ? e.message.join(', ') : e.message) : `Error ${res.status}`);
  }
  if (res.status === 204) return undefined as unknown as T;
  return res.json();
}

const unwrap = (j: any): any[] => (Array.isArray(j) ? j : (j?.data ?? j?.items ?? []));

// List API caps page size at 100; page through to fetch all.
async function fetchAllPages<T = any>(path: string): Promise<T[]> {
  const all: T[] = [];
  let page = 1;
  for (;;) {
    const sep = path.includes('?') ? '&' : '?';
    const resp = await api<any>(`${path}${sep}page=${page}&limit=100`);
    const rows = unwrap(resp);
    all.push(...(rows as T[]));
    if (!resp?.meta?.hasNext || rows.length === 0 || page > 100) break;
    page++;
  }
  return all;
}

const fmtCost = (v: number | string | null | undefined) =>
  v === null || v === undefined || v === '' ? '∞' : `₹${Number(v).toLocaleString('en-IN')} L`;

const AuthoritySlabsManagement: React.FC = () => {
  const [slabs, setSlabs] = useState<Slab[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState<'' | ActionType>('');
  const [schemeFilter, setSchemeFilter] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<SlabForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<Slab | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Resolver tester
  const [rsAction, setRsAction] = useState<ActionType>('AS');
  const [rsCost, setRsCost] = useState('');
  const [rsScheme, setRsScheme] = useState('ALL');
  const [rsResult, setRsResult] = useState<string | null>(null);
  const [rsLoading, setRsLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const rows = await fetchAllPages<Slab>('/authority-slabs');
      setSlabs(rows);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load authority slabs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(null), 3500);
    return () => clearTimeout(t);
  }, [notice]);

  const schemes = useMemo(
    () => Array.from(new Set(slabs.map((s) => s.scheme))).sort(),
    [slabs],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return slabs
      .filter((s) => {
        if (actionFilter && s.action_type !== actionFilter) return false;
        if (schemeFilter && s.scheme !== schemeFilter) return false;
        if (q && ![s.authority, s.scheme, s.action_type].some((v) => String(v).toLowerCase().includes(q))) return false;
        return true;
      })
      .sort((a, b) =>
        a.scheme.localeCompare(b.scheme) ||
        a.action_type.localeCompare(b.action_type) ||
        Number(a.min_cost) - Number(b.min_cost),
      );
  }, [slabs, search, actionFilter, schemeFilter]);

  const openCreate = () => { setEditingId(null); setForm(EMPTY_FORM); setFormError(null); setShowModal(true); };
  const openEdit = (s: Slab) => {
    setEditingId(s.id);
    setForm({ ...s, min_cost: Number(s.min_cost), max_cost: s.max_cost === null ? null : Number(s.max_cost) });
    setFormError(null);
    setShowModal(true);
  };
  const setField = (k: keyof SlabForm, v: any) => setForm((p) => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.action_type) { setFormError('Action type is required'); return; }
    if (!form.authority?.trim()) { setFormError('Authority is required'); return; }
    const min = Number(form.min_cost) || 0;
    const max = form.max_cost === null || form.max_cost === undefined || (form.max_cost as any) === '' ? null : Number(form.max_cost);
    if (max !== null && max < min) { setFormError('Max cost cannot be less than min cost'); return; }
    try {
      setSaving(true);
      setFormError(null);
      const payload = {
        scheme: (form.scheme || 'ALL').trim() || 'ALL',
        action_type: form.action_type,
        min_cost: min,
        max_cost: max,
        authority: form.authority.trim(),
        is_active: form.is_active !== false,
      };
      if (editingId) {
        await api(`/authority-slabs/${editingId}`, { method: 'PUT', body: JSON.stringify(payload) });
        setNotice('Slab updated successfully');
      } else {
        await api('/authority-slabs', { method: 'POST', body: JSON.stringify(payload) });
        setNotice('Slab created successfully');
      }
      setShowModal(false);
      await load();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Failed to save slab');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await api(`/authority-slabs/${deleteTarget.id}`, { method: 'DELETE' });
      setNotice('Slab deleted successfully');
      setDeleteTarget(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete slab');
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const runResolve = async () => {
    if (rsCost === '') { setRsResult('Enter a cost'); return; }
    try {
      setRsLoading(true);
      setRsResult(null);
      const r = await api<any>(`/authority-slabs/resolve?action=${rsAction}&cost=${Number(rsCost)}&scheme=${encodeURIComponent(rsScheme || 'ALL')}`);
      setRsResult(r?.authority ? `→ ${r.authority}` : '→ No matching slab');
    } catch (e) {
      setRsResult(e instanceof Error ? e.message : 'Resolve failed');
    } finally {
      setRsLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex items-center">
          <Scale className="w-7 h-7 text-amber-600 mr-3" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Authority Slabs</h1>
            <p className="text-sm text-gray-500">Delegation of powers — who sanctions AS / TS / Tender by cost slab</p>
          </div>
        </div>
        <button onClick={openCreate}
          className="inline-flex items-center px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors">
          <Plus className="w-4 h-4 mr-2" /> Add Slab
        </button>
      </div>

      {notice && (
        <div className="mb-4 flex items-center bg-green-50 border border-green-200 text-green-800 rounded-lg px-4 py-2 text-sm">
          <CheckCircle className="w-4 h-4 mr-2" /> {notice}
        </div>
      )}
      {error && (
        <div className="mb-4 flex items-center bg-red-50 border border-red-200 text-red-800 rounded-lg px-4 py-2 text-sm">
          <AlertCircle className="w-4 h-4 mr-2" /> {error}
        </div>
      )}

      {/* Resolver tester */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
        <div className="flex items-center text-sm font-medium text-amber-800 mb-3">
          <Calculator className="w-4 h-4 mr-2" /> Resolve competent authority
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Action</label>
            <select value={rsAction} onChange={(e) => setRsAction(e.target.value as ActionType)} className={`${inp} w-56`}>
              {ACTIONS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Cost (₹ lakhs)</label>
            <input type="number" value={rsCost} onChange={(e) => setRsCost(e.target.value)} className={`${inp} w-32`} placeholder="e.g. 75" />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Scheme</label>
            <input value={rsScheme} onChange={(e) => setRsScheme(e.target.value)} className={`${inp} w-32`} placeholder="ALL" />
          </div>
          <button onClick={runResolve} disabled={rsLoading}
            className="inline-flex items-center px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-60">
            {rsLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Resolve
          </button>
          {rsResult && <span className="text-sm font-semibold text-gray-900 ml-1">{rsResult}</span>}
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div className="relative">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search authority / scheme…"
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm" />
        </div>
        <select value={actionFilter} onChange={(e) => setActionFilter(e.target.value as any)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm">
          <option value="">All Actions</option>
          {ACTIONS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
        </select>
        <select value={schemeFilter} onChange={(e) => setSchemeFilter(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm">
          <option value="">All Schemes</option>
          {schemes.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-500"><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading slabs…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            <Scale className="w-10 h-10 mx-auto mb-2 text-gray-300" />
            {slabs.length ? 'No slabs match your filters.' : 'No slabs yet. Click “Add Slab”.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Scheme', 'Action', 'Cost Range', 'Authority', 'Active', ''].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((s) => (
                  <tr key={s.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-700 font-medium">{s.scheme}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${ACTION_BADGE[s.action_type]}`} title={ACTION_LABEL[s.action_type]}>
                        {s.action_type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{fmtCost(s.min_cost)} – {fmtCost(s.max_cost)}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">{s.authority}</td>
                    <td className="px-4 py-3">
                      {s.is_active
                        ? <span className="text-xs text-green-700 bg-green-100 px-1.5 py-0.5 rounded">Active</span>
                        : <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">Inactive</span>}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => openEdit(s)} title="Edit" className="p-1.5 text-gray-500 hover:text-amber-600 hover:bg-amber-50 rounded"><Edit2 className="w-4 h-4" /></button>
                      <button onClick={() => setDeleteTarget(s)} title="Delete" className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded ml-1"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <p className="text-xs text-gray-400 mt-2">{filtered.length} slab(s){(search || actionFilter || schemeFilter) ? ` (filtered from ${slabs.length})` : ''}</p>

      {/* Create / Edit modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">{editingId ? 'Edit Slab' : 'Add Slab'}</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              {formError && (
                <div className="flex items-center bg-red-50 border border-red-200 text-red-800 rounded-lg px-3 py-2 text-sm">
                  <AlertCircle className="w-4 h-4 mr-2" /> {formError}
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Action Type *">
                  <select className={inp} value={form.action_type || ''} onChange={(e) => setField('action_type', e.target.value)}>
                    {ACTIONS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                  </select>
                </Field>
                <Field label="Scheme">
                  <input className={inp} value={form.scheme || ''} onChange={(e) => setField('scheme', e.target.value.toUpperCase())} placeholder="ALL" />
                </Field>
                <Field label="Min Cost (₹ lakhs, inclusive)">
                  <input type="number" className={inp} value={form.min_cost ?? 0} onChange={(e) => setField('min_cost', e.target.value)} />
                </Field>
                <Field label="Max Cost (₹ lakhs, blank = no limit)">
                  <input type="number" className={inp} value={form.max_cost ?? ''} onChange={(e) => setField('max_cost', e.target.value === '' ? null : e.target.value)} placeholder="∞" />
                </Field>
                <div className="md:col-span-2">
                  <Field label="Competent Authority *">
                    <input className={inp} list="authority-suggestions" value={form.authority || ''} onChange={(e) => setField('authority', e.target.value)} placeholder="e.g. SE / CE / Government" autoComplete="off" />
                    <datalist id="authority-suggestions">
                      {AUTHORITY_SUGGESTIONS.map((a) => <option key={a} value={a} />)}
                    </datalist>
                  </Field>
                </div>
              </div>
              <label className="flex items-center text-sm text-gray-700">
                <input type="checkbox" className="mr-2 h-4 w-4 rounded border-gray-300 text-amber-600"
                  checked={form.is_active !== false} onChange={(e) => setField('is_active', e.target.checked)} />
                Active
              </label>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleSave} disabled={saving}
                className="inline-flex items-center px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-60">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingId ? 'Save Changes' : 'Create Slab'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center mb-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mr-3"><Trash2 className="w-5 h-5 text-red-600" /></div>
              <h2 className="text-lg font-semibold text-gray-900">Delete Slab</h2>
            </div>
            <p className="text-sm text-gray-600 mb-5">
              Delete the <span className="font-medium">{deleteTarget.action_type}</span> slab for scheme
              <span className="font-medium"> {deleteTarget.scheme}</span> ({fmtCost(deleteTarget.min_cost)}–{fmtCost(deleteTarget.max_cost)} → {deleteTarget.authority})?
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteTarget(null)} className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleDelete} disabled={deleting}
                className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-60">
                {deleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const inp = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm';

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
    {children}
  </div>
);

export default AuthoritySlabsManagement;
